import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Search, Copy, Trash2, Eye, Pencil, MoreVertical, Loader2, Sparkles, Link2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { PageHeader } from "@/components/admin/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { priceLabel, formatDate } from "@/lib/format";
import { toast } from "sonner";

export default function AdminProperties() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [state, setState] = useState("all");
  const [del, setDel] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [importing, setImporting] = useState(false);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (state !== "all") params.set("state", state);
    const r = await api.get(`/admin/properties?${params.toString()}`);
    setItems(r.data);
  }, [q, state]);

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const duplicate = async (id) => { await api.post(`/admin/properties/${id}/duplicate`); toast.success("Property duplicated"); load(); };
  const doDelete = async () => { await api.delete(`/admin/properties/${del.id}`); toast.success("Property deleted"); setDel(null); load(); };
  const publish = async (p) => { await api.put(`/admin/properties/${p.id}`, { state: p.state === "published" ? "draft" : "published" }); toast.success("Updated"); load(); };

  const runImport = async () => {
    if (!importUrl.trim()) { toast.error("Paste a property URL first"); return; }
    setImporting(true);
    try {
      const res = await api.post("/admin/properties/import", { url: importUrl.trim() });
      toast.success("Imported — review the draft and publish");
      setImportOpen(false);
      setImportUrl("");
      navigate(`/admin/properties/${res.data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Import failed");
    } finally { setImporting(false); }
  };

  return (
    <div>
      <PageHeader title="Properties" subtitle="Add, edit and manage your listings"
        action={<div className="flex gap-2">
          <Button variant="outline" onClick={() => setImportOpen(true)} className="rounded-full gap-2" data-testid="import-property-btn"><Sparkles className="w-4 h-4 text-[#C81D35]" /> Import from link</Button>
          <Link to="/admin/properties/new"><Button className="rounded-full bg-[#C81D35] hover:bg-[#a5162b] gap-2" data-testid="add-property-btn"><Plus className="w-4 h-4" /> Add property</Button></Link>
        </div>} />

      <div className="flex gap-3 mb-5 flex-wrap">
        <div className="relative flex-1 min-w-[220px]"><Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, reference, town..." className="pl-9" data-testid="property-search" /></div>
        <Select value={state} onValueChange={setState}>
          <SelectTrigger className="w-[160px]" data-testid="property-state-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All states</SelectItem><SelectItem value="published">Published</SelectItem><SelectItem value="draft">Draft</SelectItem></SelectContent>
        </Select>
      </div>

      <div className="bg-white rounded-xl border border-border overflow-hidden">
        {items === null ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-[#C81D35] animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-16" data-testid="properties-empty"><p className="text-muted-foreground">No properties found.</p><Link to="/admin/properties/new"><Button className="mt-4 rounded-full bg-[#C81D35] hover:bg-[#a5162b]">Add your first property</Button></Link></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-muted-foreground border-b border-border">
                <th className="p-4 font-medium">Property</th><th className="p-4 font-medium">Price</th><th className="p-4 font-medium">Status</th><th className="p-4 font-medium">State</th><th className="p-4 font-medium">Added</th><th className="p-4"></th>
              </tr></thead>
              <tbody>
                {items.map((p) => (
                  <tr key={p.id} className="border-b border-border/60 last:border-0 hover:bg-muted/30" data-testid={`property-row-${p.id}`}>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-14 h-11 rounded-lg bg-muted overflow-hidden shrink-0">{p.images?.[0]?.url && <img src={p.images[0].url} alt="" className="w-full h-full object-cover" />}</div>
                        <div><p className="font-medium text-[#111827] line-clamp-1">{p.title || "Untitled"}</p><p className="text-xs text-muted-foreground">{p.reference} · {[p.area, p.town].filter(Boolean).join(", ")}</p></div>
                      </div>
                    </td>
                    <td className="p-4 font-semibold text-[#C81D35] whitespace-nowrap">{priceLabel(p)}</td>
                    <td className="p-4"><span className="text-xs px-2 py-1 rounded-full bg-muted whitespace-nowrap">{p.status || "—"}</span></td>
                    <td className="p-4"><span className={`text-xs px-2 py-1 rounded-full ${p.state === "published" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>{p.state}</span></td>
                    <td className="p-4 text-muted-foreground whitespace-nowrap">{formatDate(p.created_at)}</td>
                    <td className="p-4 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`property-menu-${p.id}`}><MoreVertical className="w-4 h-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild><Link to={`/admin/properties/${p.id}`}><Pencil className="w-4 h-4 mr-2" /> Edit</Link></DropdownMenuItem>
                          {p.slug && <DropdownMenuItem asChild><Link to={`/property/${p.slug}`} target="_blank"><Eye className="w-4 h-4 mr-2" /> View</Link></DropdownMenuItem>}
                          <DropdownMenuItem onClick={() => publish(p)}>{p.state === "published" ? "Unpublish" : "Publish"}</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => duplicate(p.id)}><Copy className="w-4 h-4 mr-2" /> Duplicate</DropdownMenuItem>
                          <DropdownMenuItem className="text-red-600" onClick={() => setDel(p)}><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={importOpen} onOpenChange={(o) => !o && setImportOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#C81D35]" /> Import property from link</DialogTitle>
            <DialogDescription>Paste a link to a property on an agency's website. Homio will read the page, extract the details and images, and create a draft for you to review.</DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Link2 className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
            <Input value={importUrl} onChange={(e) => setImportUrl(e.target.value)} placeholder="https://agency.co.uk/property/..." className="pl-9" data-testid="import-url-input" onKeyDown={(e) => { if (e.key === "Enter") runImport(); }} />
          </div>
          {importing && <p className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Reading the page and extracting details… this can take up to a minute.</p>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)} className="rounded-full" disabled={importing}>Cancel</Button>
            <Button onClick={runImport} disabled={importing} className="rounded-full bg-[#C81D35] hover:bg-[#a5162b] gap-2" data-testid="run-import">{importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Import</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete this property?</AlertDialogTitle><AlertDialogDescription>"{del?.title}" will be removed from your listings. This can be restored later.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
